// src/pages/Bulletins.jsx
// 大学からの一斉連絡（教務 → 学部・学年の学生）（/messages/bulletins）【2026-10-16】
//   ・宛先は名簿の「所属」（前方一致。例「経営学部」）と「年次」で絞る。何も選ばなければ大学の学生全員
//   ・送る前に何人に届くかを数えて見せる（POST /bulletins/preview）
//   ・予約できる（60日先まで）。送った後も直せる（「編集済み」）・取り消せる。直せるのは自分が送ったものだけ
//   API: GET /bulletins/affiliations, POST /bulletins/preview, GET/POST /bulletins, PATCH/DELETE /bulletins/:id
//   使えるのは教務（academic_staff）と、開発者モードの開発者（サーバーでは教務として扱われる）
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { msgApi, loadFeatures } from '../messaging/api';
import {
  Page, Notice, Loading, Empty, CharCount, Tag, ReadBar, ScheduleField, ScheduleInput,
} from '../messaging/admin/ui';
import {
  fmtDateTime, isoToJstInput, jstInputToIso, checkSchedule, errText, MAX_TEXT,
} from '../messaging/admin/util';

const MAX_TITLE = 100;
const MAX_PREFIXES = 20;
const YEARS = [1, 2, 3, 4, 5, 6];

function audienceLabel(aud) {
  const a = aud || {};
  const affs = Array.isArray(a.affiliations) && a.affiliations.length ? a.affiliations.join('・') : 'すべての所属';
  const ys = Array.isArray(a.grade_years) && a.grade_years.length ? a.grade_years.map(y => y + '年').join('・') : 'すべての年次';
  return `${affs}／${ys}`;
}

// ── 宛先を選ぶ ─────────────────────────────────────────────
function AudiencePicker({ prefixes, setPrefixes, years, setYears }) {
  const [affs, setAffs] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('');
  const [free, setFree] = useState('');

  useEffect(() => {
    let alive = true;
    msgApi.get('/bulletins/affiliations').then(r => {
      if (!alive) return;
      if (!r.success) { setErr(errText(r)); setAffs([]); return; }
      setAffs(r.affiliations || []);
    });
    return () => { alive = false; };
  }, []);

  const shown = useMemo(() => {
    if (!affs) return [];
    const f = filter.trim();
    return f ? affs.filter(a => (a.affiliation || '').includes(f)) : affs;
  }, [affs, filter]);

  const toggle = (name) => {
    if (prefixes.includes(name)) setPrefixes(prefixes.filter(p => p !== name));
    else if (prefixes.length < MAX_PREFIXES) setPrefixes([...prefixes, name]);
  };
  const addFree = () => {
    const v = free.trim();
    if (!v) return;
    if (v.length > 100) return;
    if (!prefixes.includes(v) && prefixes.length < MAX_PREFIXES) setPrefixes([...prefixes, v]);
    setFree('');
  };
  const toggleYear = (y) => setYears(years.includes(y) ? years.filter(x => x !== y) : [...years, y].sort());

  return (
    <>
      <div className="ma-field">
        <div className="ma-field-label">
          <span>所属（選ばなければ、すべての所属）</span>
          <span className="ma-count">{prefixes.length} / {MAX_PREFIXES}</span>
        </div>
        {err && <Notice kind="error">{err}</Notice>}
        {affs === null ? <Loading text="所属を読み込み中..." /> : affs.length > 0 && (
          <>
            {affs.length > 8 && (
              <input type="search" className="ma-input ma-input--full" value={filter}
                onChange={e => setFilter(e.target.value)} placeholder="所属を絞り込む（例：経営）" />
            )}
            <div className="ma-aff-list">
              {shown.length === 0 && <div className="ma-muted ma-pad">当てはまる所属がありません。</div>}
              {shown.map(a => (
                <label className="ma-aff-row" key={a.affiliation}>
                  <input type="checkbox" checked={prefixes.includes(a.affiliation)}
                    onChange={() => toggle(a.affiliation)}
                    disabled={!prefixes.includes(a.affiliation) && prefixes.length >= MAX_PREFIXES} />
                  <span className="ma-aff-name">{a.affiliation}</span>
                  <span className="ma-aff-n">
                    {a.students}人
                    {Array.isArray(a.grade_years) && a.grade_years.length > 0 &&
                      `（${[...a.grade_years].sort((x, y) => x - y).join('・')}年）`}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
        {affs && affs.length === 0 && !err && (
          <div className="ma-muted">名簿に所属が入っている学生がいません。下の欄に所属名を入れることもできます。</div>
        )}
        <div className="ma-actions" style={{ marginTop: '0.6rem' }}>
          <input type="text" className="ma-input" style={{ flex: '1 1 200px' }} value={free} maxLength={100}
            onChange={e => setFree(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFree(); } }}
            placeholder="所属名の始まりで指定（例：経営学部）" />
          <button type="button" className="btn-secondary" onClick={addFree} disabled={!free.trim() || prefixes.length >= MAX_PREFIXES}>
            足す
          </button>
        </div>
        <div className="ma-hint">
          所属名の始まりが一致する学生に届きます。「経営学部」と入れると「経営学部 経営学科」なども含まれます。
        </div>
        {prefixes.length > 0 && (
          <div className="ma-chips">
            {prefixes.map(p => (
              <span className="ma-chip" key={p}>
                {p}
                <button type="button" onClick={() => setPrefixes(prefixes.filter(x => x !== p))} aria-label={`${p} を外す`}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="ma-field">
        <div className="ma-field-label"><span>年次（選ばなければ、すべての年次）</span></div>
        <div className="ma-years">
          {YEARS.map(y => (
            <label className="ma-check" key={y}>
              <input type="checkbox" checked={years.includes(y)} onChange={() => toggleYear(y)} />
              {y}年
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

// ── 書く欄 ─────────────────────────────────────────────────
function Composer({ onSent }) {
  const [prefixes, setPrefixes] = useState([]);
  const [years, setYears] = useState([]);
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [countErr, setCountErr] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [scheduled, setScheduled] = useState(false);
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const seq = useRef(0);

  const audience = useMemo(() => ({ affiliations: prefixes, grade_years: years }), [prefixes, years]);

  // 宛先を変えたら、少し待ってから人数を数え直す
  useEffect(() => {
    const my = ++seq.current;
    setCounting(true);
    const t = setTimeout(async () => {
      const r = await msgApi.post('/bulletins/preview', { audience });
      if (my !== seq.current) return;
      setCounting(false);
      if (!r.success) { setCountErr(errText(r)); setCount(null); return; }
      setCountErr('');
      setCount(r.count);
    }, 400);
    return () => clearTimeout(t);
  }, [audience]);

  const send = async () => {
    setMsg(null);
    const t = title.trim(), b = text.trim();
    if (!t || !b) { setMsg({ kind: 'error', text: '件名と本文を入れてください' }); return; }
    if (t.length > MAX_TITLE) { setMsg({ kind: 'error', text: `件名は${MAX_TITLE}文字までです` }); return; }
    if (b.length > MAX_TEXT) { setMsg({ kind: 'error', text: `本文は${MAX_TEXT}文字までです` }); return; }
    let sendAt = null;
    if (scheduled) {
      const bad = checkSchedule(when);
      if (bad) { setMsg({ kind: 'error', text: bad }); return; }
      sendAt = jstInputToIso(when);
    }
    // 送る直前にもう一度数える（名簿が変わっていることがある）
    const pv = await msgApi.post('/bulletins/preview', { audience });
    if (!pv.success) { setMsg({ kind: 'error', text: errText(pv) }); return; }
    setCount(pv.count);
    if (pv.count === 0 && !sendAt) {
      setMsg({ kind: 'error', text: 'この宛先に当てはまる学生がいません。宛先を見直してください' });
      return;
    }
    const q = sendAt
      ? `「${t}」を ${fmtDateTime(sendAt)} に送るよう予約します。\n宛先：${audienceLabel(audience)}\nいまの名簿では ${pv.count}人です（実際の宛先は送る時点の名簿で決まります）。\nよろしいですか？`
      : `「${t}」を ${pv.count}人に今すぐ送ります。\n宛先：${audienceLabel(audience)}\nよろしいですか？`;
    if (!window.confirm(q)) return;

    setBusy(true);
    const r = await msgApi.post('/bulletins', sendAt ? { title: t, text: b, audience, send_at: sendAt } : { title: t, text: b, audience });
    setBusy(false);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    setTitle(''); setText(''); setScheduled(false); setWhen('');
    const bl = r.bulletin || {};
    setMsg({ kind: 'ok', text: bl.scheduled ? '予約しました' : `${bl.recipients != null ? bl.recipients : pv.count}人に送りました` });
    onSent();
  };

  return (
    <div className="card">
      <div className="card-header"><h2 style={{ margin: 0 }}>一斉連絡を書く</h2></div>
      <p className="ma-lead">
        名簿の所属と年次で宛先を決めます。学生は返事できません。問い合わせ先が必要なときは本文に書いてください。
      </p>

      <AudiencePicker prefixes={prefixes} setPrefixes={setPrefixes} years={years} setYears={setYears} />

      <div className="ma-recipients" aria-live="polite">
        <span>届く人数</span>
        <span className="ma-recipients-n">{count == null ? '—' : count}</span>
        <span>人</span>
        {counting && <span className="ma-hint">数えています...</span>}
        {!counting && prefixes.length === 0 && years.length === 0 && <span className="ma-hint">（大学の学生全員）</span>}
      </div>
      {countErr && <Notice kind="error">{countErr}</Notice>}

      <div className="ma-field">
        <div className="ma-field-label"><span>件名</span><CharCount value={title} max={MAX_TITLE} /></div>
        <input type="text" className="ma-input ma-input--full" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="例）後期の履修登録期間について" disabled={busy} />
        <div className="ma-hint">件名は通知にもそのまま出ます。</div>
      </div>
      <div className="ma-field">
        <div className="ma-field-label"><span>本文</span><CharCount value={text} max={MAX_TEXT} /></div>
        <textarea className="ma-textarea" style={{ minHeight: '9rem' }} value={text} onChange={e => setText(e.target.value)} disabled={busy} />
      </div>
      <ScheduleField enabled={scheduled} onToggle={setScheduled} value={when} onChange={setWhen} idPrefix="bl" />

      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      <div className="ma-actions ma-actions--end">
        <button type="button" className="btn-primary" onClick={send} disabled={busy || !title.trim() || !text.trim()}>
          {busy ? '送っています...' : scheduled ? '予約する' : '送る'}
        </button>
      </div>
    </div>
  );
}

// ── 1件 ─────────────────────────────────────────────────────
function BulletinItem({ b, onChanged, onNotice }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(b.title || '');
  const [text, setText] = useState(b.text || '');
  const [when, setWhen] = useState(isoToJstInput(b.send_at));
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setTitle(b.title || ''); setText(b.text || ''); setWhen(isoToJstInput(b.send_at)); setEditing(true);
  };

  const save = async () => {
    const t = title.trim(), x = text.trim();
    if (!t || !x) { onNotice({ kind: 'error', text: '件名と本文を入れてください' }); return; }
    const payload = { title: t, text: x };
    if (b.scheduled) {
      const bad = checkSchedule(when);
      if (bad) { onNotice({ kind: 'error', text: bad }); return; }
      payload.send_at = jstInputToIso(when);
    } else if (!window.confirm('送った後に直すと「編集済み」と表示されます。通知はし直しません。直しますか？')) {
      return;
    }
    setBusy(true);
    const r = await msgApi.patch('/bulletins/' + b.id, payload);
    setBusy(false);
    if (!r.success) { onNotice({ kind: 'error', text: errText(r) }); return; }
    setEditing(false);
    onNotice({ kind: 'ok', text: '直しました' });
    onChanged();
  };

  const remove = async () => {
    const q = b.scheduled
      ? `予約している「${b.title}」を消しますか？（まだ誰にも届いていません）`
      : `「${b.title}」を取り消しますか？学生の画面から見えなくなります。`;
    if (!window.confirm(q)) return;
    setBusy(true);
    const r = await msgApi.del('/bulletins/' + b.id);
    setBusy(false);
    if (!r.success) { onNotice({ kind: 'error', text: errText(r) }); return; }
    onNotice({ kind: 'ok', text: b.scheduled ? '予約を消しました' : '取り消しました' });
    onChanged();
  };

  const cls = 'ma-item' + (b.scheduled ? ' ma-item--scheduled' : '') + (b.retracted ? ' ma-item--retracted' : '');
  return (
    <div className={cls}>
      <div className="ma-item-head">
        {b.scheduled
          ? <><Tag tone="blue">予約</Tag><span>{fmtDateTime(b.send_at)} に送ります</span></>
          : <span>{fmtDateTime(b.sent_at || b.send_at)}</span>}
        {b.edited && <Tag tone="gray">編集済み</Tag>}
        {b.retracted && <Tag tone="red">取り消し済み</Tag>}
        {b.author && <span>送った人：{b.author.name}{b.mine ? '（あなた）' : ''}</span>}
      </div>
      <div className="ma-muted" style={{ fontSize: '0.8rem', marginBottom: '0.4rem' }}>宛先：{audienceLabel(b.audience)}</div>

      {editing ? (
        <div className="ma-item-edit">
          <div className="ma-field">
            <div className="ma-field-label"><span>件名</span><CharCount value={title} max={MAX_TITLE} /></div>
            <input type="text" className="ma-input ma-input--full" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="ma-field">
            <div className="ma-field-label"><span>本文</span><CharCount value={text} max={MAX_TEXT} /></div>
            <textarea className="ma-textarea" value={text} onChange={e => setText(e.target.value)} />
          </div>
          {b.scheduled && (
            <div className="ma-field">
              <div className="ma-field-label"><span>送る日時（日本時間）</span></div>
              <ScheduleInput value={when} onChange={setWhen} />
            </div>
          )}
          <div className="ma-actions ma-actions--end">
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)} disabled={busy}>やめる</button>
            <button type="button" className="btn-primary" onClick={save} disabled={busy || !title.trim() || !text.trim()}>
              {busy ? '保存中...' : '保存する'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="ma-item-title">{b.title}</div>
          {b.retracted
            ? <div className="ma-item-text ma-item-text--gone">この連絡は取り消しました。</div>
            : <div className="ma-item-text">{b.text}</div>}
        </>
      )}

      {!b.retracted && !editing && (
        <div className="ma-item-foot">
          {b.scheduled
            ? <span className="ma-muted" style={{ fontSize: '0.84rem' }}>まだ送っていません（宛先は送る時点の名簿で決まります）</span>
            : <ReadBar read={b.read_count} total={b.recipients} />}
          {b.mine && (
            <div className="ma-actions">
              <button type="button" className="btn-secondary ma-btn-sm" onClick={startEdit} disabled={busy}>直す</button>
              <button type="button" className="btn-danger ma-btn-sm" onClick={remove} disabled={busy}>
                {b.scheduled ? '予約を消す' : '取り消す'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 画面 ─────────────────────────────────────────────────────
export default function Bulletins({ user }) {
  const allowed = user && ['academic_staff', 'developer'].includes(user.role);
  const [featureOn, setFeatureOn] = useState(null);
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let alive = true;
    loadFeatures().then(f => { if (alive) setFeatureOn(!!(f.features && f.features.bulletins)); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    const r = await msgApi.get('/bulletins');
    if (!r.success) { setErr(errText(r)); setList([]); return; }
    setErr('');
    setList(r.bulletins || []);
  }, []);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const lead = '学部・学年を選んで、学生にまとめて連絡します。読んだ人の数が分かります。';
  if (!allowed) {
    return (
      <Page title="大学からの一斉連絡" lead={lead}>
        <Notice kind="warn">この画面は教務の方だけが使えます。</Notice>
      </Page>
    );
  }

  return (
    <Page title="大学からの一斉連絡" lead={lead}>
      {featureOn === false && (
        <Notice kind="warn">一斉連絡は、まだ使えるようになっていません。使えるようになるまでお待ちください。</Notice>
      )}
      {featureOn && !err && <Composer onSent={load} />}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>これまでの一斉連絡</h2>
          <button type="button" className="btn-secondary ma-btn-sm" onClick={load}>最新にする</button>
        </div>
        {notice && <Notice kind={notice.kind} onClose={() => setNotice(null)}>{notice.text}</Notice>}
        {err && <Notice kind="error">{err}</Notice>}
        {list === null ? <Loading /> : list.length === 0 ? (
          !err && <Empty>まだ一斉連絡はありません。</Empty>
        ) : (
          <div className="ma-list">
            {list.map(b => (
              <BulletinItem key={b.id + ':' + (b.edited ? 'e' : '') + ':' + (b.send_at || '') + ':' + (b.title || '')}
                b={b} onChanged={load} onNotice={setNotice} />
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
